import { useState } from "react";
import { DigestPanel } from "@/components/admin/DigestPanel";
import { YouTubeCard } from "@/components/admin/YouTubeCard";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, Pencil, X, Plug } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/components/admin/auth";
import { PageTitle, Panel, StatusBadge } from "@/components/admin/AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const LIMIT = { telegram: 1000, pinterest: 800, facebook: 2000, instagram: 2000 };

const toLocal = (iso) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const dayKey = (iso) => new Date(iso).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });

const EditDialog = ({ post, onClose }) => {
  const qc = useQueryClient();
  const [caption, setCaption] = useState(post?.caption || "");
  const [at, setAt] = useState(post ? toLocal(post.scheduled_at) : "");
  if (!post) return null;
  const save = async () => {
    try {
      await api.put(`/admin/social/${post.id}`, { caption, scheduled_at: new Date(at).toISOString() });
      toast.success("Post updated"); qc.invalidateQueries({ queryKey: ["social"] }); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-xl" data-testid="social-edit-dialog">
        <DialogHeader><DialogTitle>Edit {post.platform === "pinterest" ? "Pinterest pin" : "Telegram post"}</DialogTitle></DialogHeader>
        <label className="text-xs font-semibold">{post.platform === "telegram" ? "Caption (HTML allowed: <b>, <i>, <code>)" : "Caption (plain text)"} · {caption.length}/{LIMIT[post.platform] || 1000}</label>
        <textarea rows={9} maxLength={LIMIT[post.platform] || 1000} value={caption} onChange={(e) => setCaption(e.target.value)} className="w-full rounded-md border border-slate-200 p-3 text-sm font-mono" data-testid="social-caption-input" />
        <label className="text-xs font-semibold">Scheduled for</label>
        <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} className="h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="social-schedule-input" />
        <div className="flex justify-end gap-2 mt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border" data-testid="social-edit-cancel-button">Cancel</button>
          <button onClick={save} className="px-5 py-2 rounded-lg bg-emerald-600 text-white font-bold" data-testid="social-save-button">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PinterestCard = ({ pin, isAdmin, refresh }) => {
  const [boards, setBoards] = useState(null);
  const connect = async () => {
    try { const r = await api.get("/admin/pinterest/connect"); window.location.href = r.data.authorization_url; } catch (e) { toast.error(errMsg(e)); }
  };
  const loadBoards = async () => { try { setBoards((await api.get("/admin/pinterest/boards")).data); } catch (e) { toast.error(errMsg(e)); } };
  const pick = async (id) => { try { await api.put("/admin/pinterest/board", { board_id: id }); toast.success("Board saved"); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const disconnect = async () => { if (!window.confirm("Disconnect Pinterest?")) return; await api.delete("/admin/pinterest"); refresh(); };
  return (
    <Panel title="Pinterest" testid="pinterest-panel">
      <p className="text-sm mb-3" data-testid="pinterest-status">
        {pin.configured ? <b className="text-emerald-700">Connected · board chosen</b>
          : pin.connected ? <b className="text-amber-700">Connected - choose a board</b>
          : pin.app_ready ? <b className="text-amber-700">App keys added - connect your Pinterest account</b>
          : <b className="text-red-600">Not set up - needs an approved Pinterest developer app</b>}
        {pin.sandbox && <span className="text-xs text-slate-500"> · sandbox mode (pins are only visible to you until Pinterest grants Standard access)</span>}
      </p>
      {!pin.app_ready && (
        <ol className="text-xs text-slate-600 list-decimal ml-5 space-y-1 mb-3" data-testid="pinterest-steps">
          <li>Convert your Pinterest account to a free Business account.</li>
          <li>Go to developers.pinterest.com → My apps → Connect app, and request access (approval can take a few days).</li>
          <li>Add redirect URI <code className="bg-slate-100 px-1">{window.location.origin}/api/pinterest/callback</code> in the app settings.</li>
          <li>Paste the App ID and App secret in admin → Integrations &amp; Keys → Pinterest, then come back and press Connect.</li>
        </ol>
      )}
      {isAdmin && (
        <div className="flex flex-wrap gap-2 items-center">
          {pin.app_ready && !pin.connected && <button onClick={connect} className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-semibold" data-testid="pinterest-connect-button">Connect Pinterest</button>}
          {pin.connected && <button onClick={loadBoards} className="px-3 py-1.5 rounded-lg border text-sm font-semibold" data-testid="pinterest-boards-button">Choose board</button>}
          {boards && (
            <select defaultValue={pin.board_id} onChange={(e) => pick(e.target.value)} className="h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="pinterest-board-select">
              <option value="">Select a board…</option>
              {boards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          {pin.connected && <button onClick={disconnect} className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-sm font-semibold" data-testid="pinterest-disconnect-button">Disconnect</button>}
        </div>
      )}
    </Panel>
  );
};

const MetaCard = ({ meta }) => (
  <Panel title="Facebook Page & Instagram" testid="meta-panel">
    <p className="text-sm mb-2" data-testid="meta-status">
      Facebook: <b className={meta.facebook ? "text-emerald-700" : "text-red-600"}>{meta.facebook ? "connected" : "not connected"}</b> · Instagram: <b className={meta.instagram ? "text-emerald-700" : "text-red-600"}>{meta.instagram ? "connected" : "not connected"}</b>
    </p>
    {!meta.facebook && (
      <ol className="text-xs text-slate-600 list-decimal ml-5 space-y-1">
        <li>Create a free Facebook Page for BachatRaja (and link an Instagram professional account to it if you want Instagram).</li>
        <li>At developers.facebook.com create a Business app; request <code>pages_manage_posts</code> (and <code>instagram_content_publish</code> for Instagram). Meta reviews these - it can take days.</li>
        <li>Generate a long-lived <b>Page access token</b> (never share your password) and paste the Page ID, Page token and (optional) Instagram account ID in admin → Integrations &amp; Keys.</li>
        <li>Switch on Facebook / Instagram in Settings → Social posting. Instagram captions can't contain clickable links, so posts say "link in bio".</li>
      </ol>
    )}
    <p className="text-xs text-slate-500 mt-2">WhatsApp: Meta's WhatsApp Business API cannot post to WhatsApp Channels and charges per conversation, so automatic WhatsApp posting is not included. You can share Telegram posts to WhatsApp manually.</p>
  </Panel>
);

const PostRow = ({ p, isAdmin, onEdit, refresh }) => {
  const label = { pinterest: "Pinterest", facebook: "Facebook", instagram: "Instagram" }[p.platform] || "Telegram";
  const sendNow = async () => {
    const t = toast.loading(`Posting to ${label}…`);
    try { await api.post(`/admin/social/${p.id}/send-now`); toast.success("Posted", { id: t }); } catch (e) { toast.error(errMsg(e), { id: t }); }
    refresh();
  };
  const cancel = async () => { try { await api.delete(`/admin/social/${p.id}`); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const open = p.status === "scheduled" || p.status === "failed";
  return (
    <div className="flex flex-wrap items-center gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={`social-post-${p.id}`}>
      {p.image_url && <img src={p.image_url} alt="" className="h-12 w-12 rounded object-cover" />}
      <div className="flex-1 min-w-[200px]">
        <p className="font-semibold text-sm line-clamp-1"><span className={`text-[10px] font-bold uppercase mr-2 px-1.5 py-0.5 rounded ${p.platform === "pinterest" ? "bg-red-50 text-red-700" : "bg-sky-50 text-sky-700"}`} data-testid="social-post-platform">{label}</span>{p.deal_title}</p>
        <p className="text-xs text-slate-500">{fmtDateTime(p.posted_at || p.scheduled_at)} · by {p.created_by}{p.attempts > 0 && ` · attempts ${p.attempts}`}{p.is_demo && <b className="text-purple-600"> · DEMO</b>}</p>
        {p.error && <p className="text-xs text-red-600">{p.error}</p>}
      </div>
      <StatusBadge s={p.status} />
      {open && (
        <div className="flex gap-1">
          <button onClick={() => onEdit(p)} className="p-2 rounded hover:bg-slate-100" title="Edit" data-testid="social-edit-button"><Pencil size={15} /></button>
          {isAdmin && <button onClick={sendNow} className="p-2 rounded hover:bg-emerald-50 text-emerald-700" title="Send now" data-testid="social-send-now-button"><Send size={15} /></button>}
          <button onClick={cancel} className="p-2 rounded hover:bg-red-50 text-red-600" title="Cancel" data-testid="social-cancel-button"><X size={15} /></button>
        </div>
      )}
    </div>
  );
};

export default function SocialPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [edit, setEdit] = useState(null);
  const [show, setShow] = useState("upcoming");
  const [platform, setPlatform] = useState("all");
  const { data } = useQuery({ queryKey: ["social"], queryFn: () => api.get("/admin/social").then((r) => r.data), refetchInterval: 20000 });
  const refresh = () => qc.invalidateQueries({ queryKey: ["social"] });
  const test = async () => {
    try { await api.post("/admin/social/test"); toast.success("Test message sent to your channel"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!data) return null;
  const posts = data.posts.filter((p) => (platform === "all" || (p.platform || "telegram") === platform) && (show === "upcoming" ? ["scheduled", "failed"].includes(p.status) : ["posted", "cancelled"].includes(p.status)));
  if (show === "upcoming") posts.sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
  const groups = posts.reduce((acc, p) => { const k = dayKey(p.posted_at || p.scheduled_at); (acc[k] = acc[k] || []).push(p); return acc; }, {});
  const s = data.settings;
  return (
    <div className="space-y-6" data-testid="admin-social-page">
      <PageTitle title="Social posting" sub="Deals are posted to Telegram, Pinterest, Facebook and Instagram (and YouTube Shorts from Social Media Manager) with your tracked affiliate link (clicks show the platform as source). Duplicates are blocked, and posts respect your daily limit, spacing and quiet hours.">
        {user.role === "admin" && <button onClick={test} disabled={!data.configured} data-testid="social-test-button" className="bg-slate-900 text-white font-semibold px-4 py-2 rounded-lg disabled:opacity-40 flex items-center gap-2"><Plug size={16} /> Send test message</button>}
      </PageTitle>
      <div className="grid md:grid-cols-3 gap-4 text-sm">
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="social-connection-status">
          <p className="text-xs uppercase font-semibold text-slate-500">Telegram connection</p>
          <p className={`font-bold mt-1 ${data.configured ? "text-emerald-700" : "text-red-600"}`}>{data.configured ? "Bot connected" : "Not connected - bot token / channel missing"}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs uppercase font-semibold text-slate-500">Auto-posting</p>
          <p className="font-bold mt-1">Telegram {s.enabled ? "on" : "off"} ({s.max_posts_per_day}/day) · Pinterest {s.pinterest_enabled ? "on" : "off"} ({s.pinterest_max_per_day}/day) · Facebook {s.facebook_enabled ? "on" : "off"} ({s.facebook_max_per_day}/day) · Instagram {s.instagram_enabled ? "on" : "off"} ({s.instagram_max_per_day}/day) · every ≥{s.min_gap_minutes} min</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs uppercase font-semibold text-slate-500">Quiet hours ({s.timezone})</p>
          <p className="font-bold mt-1">{s.quiet_start_hour}:00 – {s.quiet_end_hour}:00 · min score {s.min_score}</p>
        </div>
      </div>
      <p className="text-xs text-slate-500">Change these in Settings → Social posting. Turn on the "Plan social posts" and "Publish social posts" automations to run hands-free, or queue deals yourself from the Deals page (send / pin icons).</p>
      <DigestPanel isAdmin={user.role === "admin"} connected={data.configured} />
      <PinterestCard pin={data.pinterest} isAdmin={user.role === "admin"} refresh={refresh} />
      {data.youtube && <YouTubeCard yt={data.youtube} isAdmin={user.role === "admin"} refresh={refresh} />}
      {data.meta && <MetaCard meta={data.meta} />}
      <div className="flex flex-wrap gap-1">
        {[["upcoming", "Content calendar"], ["history", "Posted & cancelled"]].map(([k, l]) => (
          <button key={k} onClick={() => setShow(k)} data-testid={`social-tab-${k}`} className={`px-4 h-10 rounded-lg text-sm font-semibold ${show === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200"}`}>{l}</button>
        ))}
        <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="h-10 rounded-lg border border-slate-200 px-3 text-sm ml-auto" data-testid="social-platform-filter">
          <option value="all">All platforms</option><option value="telegram">Telegram</option><option value="pinterest">Pinterest</option><option value="facebook">Facebook</option><option value="instagram">Instagram</option>
        </select>
      </div>
      {Object.keys(groups).length === 0 && <div className="text-center py-12 border border-dashed border-slate-300 rounded-xl text-slate-500 bg-white" data-testid="social-empty">Nothing here yet.</div>}
      {Object.entries(groups).map(([day, list]) => (
        <Panel key={day} title={day} testid={`social-day-${day.replace(/\W+/g, "-")}`}>
          {list.map((p) => <PostRow key={p.id} p={p} isAdmin={user.role === "admin"} onEdit={setEdit} refresh={refresh} />)}
        </Panel>
      ))}
      {edit && <EditDialog post={edit} onClose={() => setEdit(null)} />}
    </div>
  );
}
